import { useMemo } from "react";
import type { StreamSnapshot } from "@launch10/use-langgraph-stream";
import { useStreamSelector } from "@launch10/use-langgraph-stream";
import type { SupportGraphState } from "@shared";
import { useProjectStreamOptions } from "@hooks/useProjectStreamOptions";

export type SupportSnapshot = StreamSnapshot<SupportGraphState>;

const INITIAL_GREETING =
  "Hi! I can help answer questions about Launch10. What would you like to know?";

export function useSupportStreamOptions() {
  return useProjectStreamOptions<SupportGraphState>({
    assistantId: "support",
  });
}

export function useSupportSelector<T>(
  selector: (snapshot: SupportSnapshot) => T
): T {
  return useStreamSelector<T, SupportGraphState>(selector);
}

export function useSupportMessages() {
  const messages = useSupportSelector((s) => s.messages);
  const isStreaming = useSupportSelector((s) => s.isLoading);

  return useMemo(() => {
    // Don't show greeting while streaming the first response
    if (messages.length === 0 && isStreaming) return messages;

    const greeting = {
      id: "initial-greeting",
      type: "ai" as const,
      content: INITIAL_GREETING,
    };
    return [greeting, ...messages];
  }, [messages, isStreaming]);
}

export function useSupportIsStreaming() {
  return useSupportSelector((s) => s.isLoading);
}
